import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  TrophyIcon,
  ArrowTrendingUpIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'
import { TrophyIcon as TrophySolidIcon } from '@heroicons/react/24/solid'
import { Card, CardHeader, Badge, BadgeIcon, Avatar, InlineSpinner, EmptyState } from '@/components/ui'
import { gamificationApi } from '@/api'
import type { LeaderboardEntry, UserLevel, Badge as BadgeType, PointTransaction } from '@/api/gamification'

type Period = 'weekly' | 'monthly' | 'all_time'

export default function LeaderboardPage() {
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([])
  const [myStats, setMyStats] = useState<{
    level: UserLevel
    badges: BadgeType[]
    recent_points: PointTransaction[]
    rank: number
  } | null>(null)
  const [myRank, setMyRank] = useState<{
    rank: number
    total_users: number
    percentile: number
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const [period, setPeriod] = useState<Period>('monthly')

  useEffect(() => {
    fetchData()
  }, [period])

  const fetchData = async () => {
    setLoading(true)
    try {
      const [leaderboardData, statsData, rankData] = await Promise.all([
        gamificationApi.getLeaderboard({ period, limit: 50 }),
        gamificationApi.getMyStats(),
        gamificationApi.getMyRank(period),
      ])
      setLeaderboard(leaderboardData)
      setMyStats(statsData)
      setMyRank(rankData)
    } catch (error) {
      console.error('Error fetching leaderboard:', error)
    } finally {
      setLoading(false)
    }
  }

  const getRankBadge = (rank: number) => {
    if (rank === 1) return { color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: '🥇' }
    if (rank === 2) return { color: 'bg-gray-100 text-gray-700 border-gray-300', icon: '🥈' }
    if (rank === 3) return { color: 'bg-orange-100 text-orange-700 border-orange-300', icon: '🥉' }
    return { color: 'bg-gray-50 text-gray-600', icon: null }
  }

  const getPeriodLabel = (p: Period) => {
    const labels: Record<Period, string> = {
      weekly: 'Questa settimana',
      monthly: 'Questo mese',
      all_time: 'Di sempre',
    }
    return labels[p]
  }

  if (loading) {
    return <InlineSpinner message="Caricamento classifica..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Classifica</h1>
          <p className="text-gray-600">Scopri i membri più attivi della community</p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/gamification/badges"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <SparklesIcon className="h-5 w-5 mr-2" />
            I miei Badge
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Leaderboard */}
        <div className="lg:col-span-2 space-y-6">
          {/* Period Filter */}
          <Card>
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-700">Periodo:</span>
              <div className="flex gap-2">
                {(['weekly', 'monthly', 'all_time'] as Period[]).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPeriod(p)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      period === p
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {getPeriodLabel(p)}
                  </button>
                ))}
              </div>
            </div>
          </Card>

          {/* Leaderboard Table */}
          {leaderboard.length > 0 ? (
            <Card>
              <CardHeader title="Top 50" subtitle={getPeriodLabel(period)} />
              <div className="space-y-2">
                {leaderboard.map((entry, index) => {
                  const rankStyle = getRankBadge(entry.rank)

                  return (
                    <div
                      key={entry.user.id}
                      className={`flex items-center gap-4 p-3 rounded-lg transition-colors ${
                        index < 3 ? rankStyle.color + ' border' : 'hover:bg-gray-50'
                      }`}
                    >
                      {/* Rank */}
                      <div className="w-10 flex-shrink-0 text-center">
                        {rankStyle.icon ? (
                          <span className="text-2xl">{rankStyle.icon}</span>
                        ) : (
                          <span className="text-lg font-bold text-gray-400">#{entry.rank}</span>
                        )}
                      </div>

                      {/* User */}
                      <Avatar
                        size="md"
                        name={entry.user.full_name}
                        src={entry.user.avatar || undefined}
                      />

                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{entry.user.full_name}</p>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <span>Livello {entry.level}</span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <SparklesIcon className="h-3 w-3" />
                            {entry.badges_count} badge
                          </span>
                        </div>
                      </div>

                      {/* Points */}
                      <div className="text-right">
                        <p className="font-bold text-primary-600">{entry.points.toLocaleString()}</p>
                        <p className="text-xs text-gray-500">punti</p>
                      </div>
                    </div>
                  )
                })}
              </div>
            </Card>
          ) : (
            <Card>
              <EmptyState
                icon={<TrophyIcon className="h-12 w-12" />}
                title="Classifica vuota"
                description="Nessun dato disponibile per questo periodo"
              />
            </Card>
          )}
        </div>

        {/* Sidebar - My Stats */}
        <div className="space-y-6">
          {/* My Position */}
          {myRank && (
            <Card>
              <CardHeader title="La tua posizione" />
              <div className="text-center space-y-4">
                <div className="w-20 h-20 mx-auto bg-primary-100 rounded-full flex items-center justify-center">
                  <TrophySolidIcon className="h-10 w-10 text-primary-600" />
                </div>
                <div>
                  <p className="text-4xl font-bold text-gray-900">#{myRank.rank}</p>
                  <p className="text-sm text-gray-500">su {myRank.total_users} utenti</p>
                </div>
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-center gap-2">
                    <ArrowTrendingUpIcon className="h-5 w-5 text-green-500" />
                    <span className="text-sm text-gray-600">
                      Sei nel top <span className="font-semibold text-green-600">{myRank.percentile}%</span>
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* My Level */}
          {myStats && (
            <Card>
              <CardHeader title="Il tuo livello" />
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-lg">{myStats.level.level}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Livello {myStats.level.level}</p>
                      <p className="text-sm text-gray-500">{myStats.level.current_points} punti totali</p>
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div>
                  <div className="flex justify-between text-sm text-gray-500 mb-1">
                    <span>Prossimo livello</span>
                    <span>{myStats.level.progress_percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full transition-all"
                      style={{ width: `${myStats.level.progress_percentage}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {myStats.level.next_level_points - myStats.level.total_points} punti mancanti
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Recent Badges */}
          {myStats && myStats.badges.length > 0 && (
            <Card>
              <CardHeader title="Badge recenti" />
              <div className="flex flex-wrap gap-2">
                {myStats.badges.slice(0, 6).map((badge) => (
                  <div
                    key={badge.id}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg"
                    title={badge.description}
                  >
                    <BadgeIcon icon={badge.icon} className="w-6 h-6" />
                    <span className="text-sm font-medium text-gray-700">{badge.name}</span>
                  </div>
                ))}
              </div>
              {myStats.badges.length > 6 && (
                <Link
                  to="/gamification/badges"
                  className="block text-center text-sm text-primary-600 hover:text-primary-700 mt-4"
                >
                  Vedi tutti i badge →
                </Link>
              )}
            </Card>
          )}

          {/* Recent Points */}
          {myStats && myStats.recent_points.length > 0 && (
            <Card>
              <CardHeader title="Punti recenti" />
              <div className="space-y-3">
                {myStats.recent_points.slice(0, 5).map((transaction) => (
                  <div key={transaction.id} className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-900">{transaction.description}</p>
                      <p className="text-xs text-gray-400">
                        {new Date(transaction.created_at).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                    <Badge variant={transaction.points > 0 ? 'success' : 'default'}>
                      {transaction.points > 0 ? '+' : ''}{transaction.points}
                    </Badge>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
